import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Descriptions,
  Avatar,
  Tag,
  Spin,
  message,
  Divider,
  Tabs,
  Table,
  Empty,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  IdcardOutlined,
  CalendarOutlined,
  LinkOutlined,
  CopyOutlined,
  GiftOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../../const";

const ProfileWrapper = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: calc(100vh - 64px);

  .back-button {
    margin-bottom: 16px;
    color: #1890ff;
    font-weight: 500;
  }

  .profile-header {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-bottom: 24px;
  }

  .profile-avatar {
    width: 100px;
    height: 100px;
    font-size: 40px;
    background: linear-gradient(135deg, #00d9a9 0%, #014c75 100%);
  }

  .profile-info {
    flex: 1;
  }

  .profile-name {
    font-size: 28px;
    font-weight: 600;
    color: #014c75;
    margin-bottom: 8px;
  }

  .profile-role {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .profile-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    margin-bottom: 24px;
  }

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
  }

  .org-link {
    color: #1890ff;
    cursor: pointer;
  }

  .org-link:hover {
    text-decoration: underline;
  }

  .referral-code-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    padding: 14px 16px;
    background: #f6f9fc;
    border-radius: 8px;
    border: 1px solid #e8f0f8;
  }
`;

const referralColumns = [
  {
    title: "Customer Org",
    dataIndex: "customerOrganization",
    key: "customerOrganization",
    render: (v) => <strong style={{ color: "#014c75" }}>{v || "—"}</strong>,
  },
  {
    title: "Contact Name",
    dataIndex: "contactName",
    key: "contactName",
    render: (v) => v || "—",
  },
  {
    title: "Contact Email",
    dataIndex: "contactEmail",
    key: "contactEmail",
    render: (v) =>
      v ? <a href={`mailto:${v}`} style={{ color: "#1677ff" }}>{v}</a> : "—",
  },
  {
    title: "Date Joined",
    dataIndex: "dateJoined",
    key: "dateJoined",
    render: (v) =>
      v
        ? new Date(v).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "—",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (v) => {
      const colorMap = { active: "green", inactive: "default", pending: "orange" };
      return <Tag color={colorMap[v?.toLowerCase()] || "default"}>{v || "—"}</Tag>;
    },
  },
];

const UserProfileView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [referrals, setReferrals] = useState([]);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralLoaded, setReferralLoaded] = useState(false);
  const [referralCode, setReferralCode] = useState(null);

  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersLoaded, setPartnersLoaded] = useState(false);

  const backPath = location.state?.from || "/admin/user-management";
  const backLabel = location.state?.fromLabel || "Back to User Management";

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_CONST.GET_USER}/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.user) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      message.error("Failed to load user details");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = useCallback(async () => {
    if (referralLoaded || !user?.organization) return;
    setReferralLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(
        `${API_CONST.REFERRAL_BY_PARTNER}/${encodeURIComponent(user.organization)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReferrals(res.data.customers || []);
      setReferralCode(res.data.referralCode || null);
      setReferralLoaded(true);
    } catch (err) {
      message.error("Failed to load referral data");
    } finally {
      setReferralLoading(false);
    }
  }, [user, referralLoaded]);

  const fetchPartners = useCallback(async () => {
    if (partnersLoaded || !user?.organization) return;
    setPartnersLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(
        `${API_CONST.PARTNER_RELATION_MY_PARTNERS}?customerOrg=${encodeURIComponent(user.organization)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPartners(res.data.relations || []);
      setPartnersLoaded(true);
    } catch (err) {
      message.error("Failed to load partner data");
    } finally {
      setPartnersLoading(false);
    }
  }, [user, partnersLoaded]);

  const handleTabChange = (key) => {
    if (key === "referrals") fetchReferrals();
    if (key === "partners") fetchPartners();
  };

  const handleOrganizationClick = () => {
    if (user?.organization) {
      navigate(`/admin/organization/${encodeURIComponent(user.organization)}`, {
        state: { from: `/admin/user-profile/${userId}`, fromLabel: "Back to User Profile" }
      });
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin": return "purple";
      case "customer": return "blue";
      case "partner": return "green";
      default: return "default";
    }
  };

  const getStatusColor = (status) =>
    status?.toLowerCase() === "active" ? "green" : "red";

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => message.success("Copied!"));
  };

  if (loading) {
    return (
      <ProfileWrapper>
        <div className="loading-container">
          <Spin size="large" />
        </div>
      </ProfileWrapper>
    );
  }

  if (!user) {
    return (
      <ProfileWrapper>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backPath)}
          className="back-button"
        >
          {backLabel}
        </Button>
        <div className="profile-card">
          <p>User not found</p>
        </div>
      </ProfileWrapper>
    );
  }

  const isPartner = user.role?.toLowerCase() === "partner";

  const profileTab = (
    <>
      <div className="profile-header">
        <Avatar className="profile-avatar" icon={<UserOutlined />} />
        <div className="profile-info">
          <div className="profile-name">{user.name}</div>
          <div className="profile-role">
            <Tag color={getRoleColor(user.role)}>{user.role?.toUpperCase()}</Tag>
            <Tag color={getStatusColor(user.status)}>{user.status}</Tag>
          </div>
        </div>
      </div>

      <Divider />

      <h3 className="section-title">Contact Information</h3>
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label={<span><MailOutlined style={{ marginRight: 8 }} />Email</span>}>
          {user.email || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={<span><PhoneOutlined style={{ marginRight: 8 }} />Phone</span>}>
          {user.contact || "-"}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <h3 className="section-title">Professional Details</h3>
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label={<span><BankOutlined style={{ marginRight: 8 }} />Organization</span>}>
          {user.organization ? (
            <span className="org-link" onClick={handleOrganizationClick}>
              {user.organization}
            </span>
          ) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={<span><IdcardOutlined style={{ marginRight: 8 }} />Designation</span>}>
          {user.designation || "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label={<span><UserOutlined style={{ marginRight: 8 }} />Assigned Role</span>}
          span={2}
        >
          {user.profileRoleName ? (
            <Tag color="blue">{user.profileRoleName}</Tag>
          ) : (
            <Tag color="default">Default ({user.role})</Tag>
          )}
        </Descriptions.Item>
        {user.role === "admin" && user.adminRole && (
          <Descriptions.Item
            label={<span><UserOutlined style={{ marginRight: 8 }} />Admin Role</span>}
            span={2}
          >
            {user.adminRole}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider />

      <h3 className="section-title">Account Details</h3>
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label={<span><CalendarOutlined style={{ marginRight: 8 }} />Created On</span>}>
          {user.createdAt
            ? new Date(user.createdAt).toLocaleDateString("en-US", {
                day: "2-digit", month: "short", year: "numeric",
              })
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={<span><CalendarOutlined style={{ marginRight: 8 }} />Last Modified</span>}>
          {user.updatedAt
            ? new Date(user.updatedAt).toLocaleDateString("en-US", {
                day: "2-digit", month: "short", year: "numeric",
              })
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="User ID" span={2}>
          {user.id || "-"}
        </Descriptions.Item>
      </Descriptions>
    </>
  );

  const referralsTab = (
    <>
      {referralLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin tip="Loading referral data…" />
        </div>
      ) : (
        <>
          <div className="referral-code-row">
            <LinkOutlined style={{ color: "#1677ff" }} />
            <span style={{ fontSize: 13, color: "#555" }}>Referral Code:</span>
            {referralCode ? (
              <>
                <Tag
                  color="blue"
                  style={{ fontFamily: "monospace", letterSpacing: 1, fontSize: 13 }}
                >
                  {referralCode}
                </Tag>
                <Tooltip title="Copy referral link">
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() =>
                      copyToClipboard(
                        `${window.location.origin}/signup?ref=${referralCode}`
                      )
                    }
                  >
                    Copy Link
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Tag color="default">No referral code assigned</Tag>
            )}
          </div>

          <div style={{ marginBottom: 12, color: "#888", fontSize: 13 }}>
            {referrals.length} customer{referrals.length !== 1 ? "s" : ""} joined via this partner's referral link
          </div>

          {referrals.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No customers have signed up via this partner's referral link yet."
            />
          ) : (
            <Table
              dataSource={referrals.map((r, i) => ({ ...r, key: i }))}
              columns={referralColumns}
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total) => `Total ${total} rows`,
              }}
              scroll={{ x: 700 }}
            />
          )}
        </>
      )}
    </>
  );

  const isCustomer = user.role?.toLowerCase() === "customer";

  const sourceTag = (src) => {
    if (src === "referral") return <Tag color="purple" icon={<GiftOutlined />}>Referral</Tag>;
    if (src === "invite") return <Tag color="geekblue">Invite</Tag>;
    if (src === "bench_share") return <Tag color="cyan">Bench Share</Tag>;
    return <Tag color="default">Manual</Tag>;
  };

  const partnerTableColumns = [
    {
      title: "Partner Org",
      dataIndex: "partnerOrganization",
      key: "partnerOrganization",
      render: (v, r) => <strong>{v || r.invitedName || "—"}</strong>,
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, r) => r.invitedName
        ? <span>{r.invitedName}{r.invitedEmail ? ` (${r.invitedEmail})` : ""}</span>
        : r.invitedEmail || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => {
        const map = { active: "green", pending_invite: "orange", inactive: "default" };
        return <Tag color={map[v] || "default"}>{v === "pending_invite" ? "Pending Invite" : v || "—"}</Tag>;
      },
    },
    {
      title: "How Connected",
      dataIndex: "source",
      key: "source",
      render: (v) => sourceTag(v),
    },
    {
      title: "Connected On",
      dataIndex: "acceptedAt",
      key: "acceptedAt",
      render: (v, r) => {
        const d = v || r.invitedAt;
        return d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
      },
    },
  ];

  const partnersTab = (
    <>
      {partnersLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin tip="Loading partner data…" />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 12, color: "#888", fontSize: 13 }}>
            {partners.length} partner{partners.length !== 1 ? "s" : ""} linked to this customer
          </div>
          {partners.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No partners linked to this customer yet."
            />
          ) : (
            <Table
              dataSource={partners.map((p, i) => ({ ...p, key: i }))}
              columns={partnerTableColumns}
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total) => `Total ${total} rows`,
              }}
              scroll={{ x: 600 }}
            />
          )}
        </>
      )}
    </>
  );

  const tabItems = [
    { key: "profile", label: "Profile", icon: <UserOutlined />, children: profileTab },
    ...(isPartner
      ? [{ key: "referrals", label: "Referrals", icon: <GiftOutlined />, children: referralsTab }]
      : []),
    ...(isCustomer
      ? [{ key: "partners", label: "Partners", icon: <TeamOutlined />, children: partnersTab }]
      : []),
  ];

  return (
    <ProfileWrapper>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(backPath)}
        className="back-button"
      >
        {backLabel}
      </Button>

      <div className="profile-card">
        <Tabs
          defaultActiveKey="profile"
          onChange={handleTabChange}
          items={tabItems}
          tabBarStyle={{ marginBottom: 24 }}
        />
      </div>
    </ProfileWrapper>
  );
};

export default UserProfileView;
